/* eslint-disable @typescript-eslint/no-shadow */
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react/react-in-jsx-scope */
import { useEffect, useState } from 'react';
import {
  Box,
  Text,
  VStack,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  HStack,
} from '@chakra-ui/react';
import {
  AudioBookEpisode,
  AudiobooksService,
  BaseAudioBookReturn,
  CompleteAudioBookReturn,
} from '../../../generated/client';
import { FaStar } from 'react-icons/fa';
import { saveAs } from 'file-saver';
import { PDFDocument } from 'pdf-lib';
import { AudioBookEpisodes } from './AudioBookEpisodes';
import { CommentSection } from './CommentSection';

/**
 * The AudioBookResultsComponent does the following:
 * -- displays a list of audiobook episodes to be viewed
 * -- displays comment section when toggled
 * -- allows rating of the given audiobook and displays average rating of audiobook
 *
 * @param props: A 'podast', which is a BaseAudioBookReturn
 *             : An 'audioBookService', which is an AudiobooksService
 */

interface Props {
  podcast: BaseAudioBookReturn;
  audioBookService: AudiobooksService;
}

export function AudioBookResultsComponent({ podcast, audioBookService }: Props): JSX.Element {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [rating, setRating] = useState(0); // current rating that is set by user
  const [audioBook, setAudioBook] = useState<CompleteAudioBookReturn>(); // current audiobook to handle updates to props
  const [avgRating, setAvgRating] = useState(0); // calculated rating based on the given podcast's existing ratings
  const [isCommentsShown, setIsCommentsShown] = useState(false);

  const openModal = () => {
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  // handler for when user submits a rating
  const handleStarSubmit = async () => {
    if (audioBook) {
      const audioBookRatings = audioBook.ratings ?? [];
      audioBookRatings.push(rating); // adds rating to temp audioBook rating array fetched from given podast
      const updatedAudioBook: CompleteAudioBookReturn = {
        id: audioBook.id,
        title: audioBook.title,
        author: audioBook.author,
        content: audioBook.content,
        comments: audioBook.comments ?? [], // comments initialized as null so this must be checked
        ratings: audioBookRatings,
      };
      setAudioBook(updatedAudioBook); // updates current audiobook with new rating
      await audioBookService.updateAudioBook(updatedAudioBook); // sends new info to database
    }
  };

  // handler for when a user clicks on a rating
  const handleStarClick = (selectedRating: number) => {
    if (selectedRating !== rating) {
      setRating(selectedRating);
    } else {
      setRating(0); // intuitive way to reset star rating to 0
    }
  };

  const handleOpenComments = () => {
    setIsCommentsShown(!isCommentsShown);
  };

  // updates average star display each time user rates an audiobook
  useEffect(() => {
    (async () => {
      if (audioBook?.ratings && audioBook.ratings.length != 0) {
        let totalRatingSum = 0;
        for (let i = 0; i < audioBook.ratings.length; i++) {
          totalRatingSum += audioBook.ratings[i];
        }
        setAvgRating(parseFloat((totalRatingSum / audioBook.ratings.length).toFixed(2)));
      } else setAvgRating(-1);
    })();
  }, [audioBook]);

  // fetches most updated version of a complete audiobook to set audioBook state
  useEffect(() => {
    (async () => {
      const completeAudioBook = await audioBookService.getAudioBook(podcast.id);
      setAudioBook(completeAudioBook);
    })();
  }, [audioBookService, podcast.id]);

  return (
    <div>
      <Box className='resultsWrap'>
        <VStack spacing={5} align='start'>
          <Text fontSize='2x1' fontWeight='bold'>
            {podcast.title}
          </Text>
          <Text fontSize='md'>{podcast.author}</Text>
          <Button colorScheme='teal' onClick={openModal}>
            Play AudioBook
          </Button>
          <HStack>
            {[1, 2, 3, 4, 5].map(index => (
              <Box key={index} aria-label={`${index} star${index === 1 ? '' : 's'}`}>
                <FaStar color={index <= Math.round(avgRating) ? 'gold' : 'gray'} />
              </Box>
            ))}
            <Text fontSize='md'>{`${
              avgRating == -1 ? 'No ratings' : avgRating + ' star' + `${rating === 1 ? '' : 's'}`
            }`}</Text>
          </HStack>
          <Text>{}</Text>
          <Modal isOpen={isModalOpen} onClose={closeModal}>
            <ModalOverlay />
            <ModalContent maxW='56rem'>
              <ModalHeader>
                {podcast.title}
                <HStack>
                  {[1, 2, 3, 4, 5].map(index => (
                    <Box
                      key={index}
                      onClick={() => handleStarClick(index)}
                      aria-label={`${index} star${index === 1 ? '' : 's'}`}>
                      <FaStar color={index <= rating ? 'gold' : 'gray'} />
                    </Box>
                  ))}
                  <Text fontSize='md'>{`${rating} star${rating === 1 ? '' : 's'}`}</Text>
                  <Button colorScheme='teal' onClick={handleStarSubmit}>
                    Rate
                  </Button>
                  <Button colorScheme='teal' onClick={handleOpenComments}>
                    View Comments
                  </Button>
                  <Text fontSize='md'>
                    Average Stars: {avgRating == -1 ? 'No ratings' : avgRating}
                  </Text>
                </HStack>
              </ModalHeader>
              <ModalCloseButton />
              <ModalBody className='horizontalWrap'>
                <div>
                  {audioBook && audioBook.content ? (
                    audioBook.content.map(ep => (
                      <AudioBookEpisodes key={ep.title} episode={ep}></AudioBookEpisodes>
                    ))
                  ) : (
                    <div>No audiobooks to list</div>
                  )}
                </div>
                <div>
                  {isCommentsShown ? (
                    <Box className='resultsWrap'>
                      <CommentSection
                        key={podcast.id}
                        completeReturn={audioBook}
                        service={audioBookService}></CommentSection>
                    </Box>
                  ) : (
                    ''
                  )}
                </div>
              </ModalBody>
            </ModalContent>
          </Modal>
        </VStack>
      </Box>
    </div>
  );
}
